import { useState, useMemo, useEffect, useId } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/shared/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import {
  useClassificationRuleTargets,
  useRuleCategories,
} from "@/features/admin/queries";
import type { CreateClassificationRuleRequest } from "@/services/api/classification-rules";
import { KeywordsInput, WeightInput, TargetsSelector } from "./components";
import { useClassificationRuleForm } from "./hooks/useClassificationRuleForm";

interface ClassificationRuleCreateDialogProps {
  open: boolean;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateClassificationRuleRequest) => void;
}

export function ClassificationRuleCreateDialog({
  open,
  isLoading,
  onOpenChange,
  onSubmit,
}: ClassificationRuleCreateDialogProps) {
  const [category, setCategory] = useState("");
  const [value, setValue] = useState("");
  const [targets, setTargets] = useState<string[]>([]);

  const { data: availableTargets = [], isLoading: isLoadingTargets } =
    useClassificationRuleTargets();
  const { data: categories = [], isLoading: isLoadingCategories } =
    useRuleCategories();

  const selectedCategory = categories.find((cat) => cat.name === category);
  const requiresValue = selectedCategory?.requiresValue ?? true;

  // BUSINESS LOGIC: Filter available targets based on selected category
  // WHEN USER SELECTS USA OR AUSTRALIA, ONLY SHOW LOCATION TARGET
  const filteredAvailableTargets = useMemo(() => {
    if (category === "USA" || category === "AUSTRALIA") {
      // Only show "Location" for USA and AUSTRALIA
      return availableTargets.filter((target) => target === "Location");
    }
    // Show all targets for other categories
    return availableTargets;
  }, [availableTargets, category]);

  // Clear invalid targets when category changes
  useEffect(() => {
    if (category === "USA" || category === "AUSTRALIA") {
      // Remove any targets that are not "Location"
      setTargets((currentTargets) => {
        const validTargets = currentTargets.filter(
          (target) => target === "Location"
        );
        return validTargets;
      });
    }
  }, [category]);

  useEffect(() => {
    if (!requiresValue) {
      setValue("");
    }
  }, [requiresValue]);

  const form = useClassificationRuleForm();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.validate() || !category) return;
    if (requiresValue && !value.trim()) return;

    onSubmit({
      category: category || "",
      value: requiresValue ? value || "" : "",
      targets,
      weight: form.getWeightValue(),
      keywords: form.keywords,
    });

    setCategory("");
    setValue("");
    setTargets([]);
    form.reset();
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setCategory("");
      setValue("");
      setTargets([]);
      form.reset();
    }
    onOpenChange(isOpen);
  };

  const descriptionId = useId();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle>Create classification rule</DialogTitle>
          <DialogDescription>
            Create a new classification rule. Fill in the required fields below.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-ink-800">
                Rule category <span className="text-red-500">*</span>
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    disabled={isLoadingCategories}
                  >
                    <span className="text-sm truncate text-left min-w-0 flex-1">
                      {selectedCategory?.displayName ||
                        category ||
                        "Select category"}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 max-h-[300px] overflow-y-auto">
                  {isLoadingCategories ? (
                    <DropdownMenuItem disabled>
                      Loading categories...
                    </DropdownMenuItem>
                  ) : categories.length === 0 ? (
                    <DropdownMenuItem disabled>
                      No categories available
                    </DropdownMenuItem>
                  ) : (
                    categories.map((cat) => (
                      <DropdownMenuItem
                        key={cat.name}
                        onClick={() => setCategory(cat.name)}
                      >
                        <span className="truncate">{cat.displayName}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {category && requiresValue && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-ink-800">
                  Value <span className="text-red-500">*</span>
                </label>
                <Input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter value"
                  required
                />
              </div>
            )}
          </div>

          <WeightInput
            value={form.weight}
            error={form.weightError}
            onChange={form.setWeight}
            onErrorChange={form.setWeightError}
          />

          <TargetsSelector
            targets={targets}
            availableTargets={filteredAvailableTargets}
            isLoading={isLoadingTargets}
            onTargetsChange={setTargets}
          />

          <KeywordsInput
            keywords={form.keywords}
            error={form.keywordsError}
            onKeywordsChange={form.setKeywords}
            disabled={isLoading}
          />

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !category}>
              {isLoading ? "Creating..." : "Create rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
